'use client';

import type { FormSchema } from '~/landing/core/simulator/future-savings-planner';

import {
  Badge,
  Card,
  Icon,
  Separator,
  SimpleCard,
  Slider,
  Typography,
} from '@polyseme/design-system';
import { cn } from '@polyseme/utils/cn/cn-utils';
import { formatCurrency } from '@polyseme/utils/number/number-utils';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { DynamicFontTypography, RippleIndicator } from '~/landing/components';
import {
  getRateByRiskProfile,
  simulateYearlyProjections,
} from '~/landing/core/simulator/future-savings-planner';

import { AnimatedNumber } from './animate-number';

const investmentDelay = {
  min: 1,
  max: 10,
  step: 1,
};
const id = 'investment-delay-slider';

export const InvestmentDelayImpact = () => {
  const t = useTranslations('landing');
  const form = useFormContext<FormSchema>();

  const [delay, setDelay] = useState<number[]>([investmentDelay.min]);

  const [
    initialDeposit,
    monthlyPayment,
    birthDate,
    retirementAge,
    riskProfile,
  ] = form.watch([
    'futureSavingsPlanner.initialDeposit',
    'futureSavingsPlanner.monthlyPayment',
    'futureSavingsPlanner.birthDate',
    'futureSavingsPlanner.retirementAge',
    'futureSavingsPlanner.riskProfile',
  ]);

  const amountAtRetirement = useMemo(() => {
    const { projections } = simulateYearlyProjections({
      birthDate,
      initialDeposit,
      monthlyPayment,
      retirementAge,
      riskProfile,
      rate: getRateByRiskProfile(riskProfile),
    });
    return projections[projections.length - 1]?.median || 0;
  }, [birthDate, initialDeposit, monthlyPayment, retirementAge, riskProfile]);

  const amountAtRetirementWithDelay = useMemo(() => {
    const { projections } = simulateYearlyProjections({
      birthDate,
      initialDeposit,
      monthlyPayment,
      retirementAge,
      riskProfile,
      rate: getRateByRiskProfile(riskProfile),
      startDelay: delay[0],
    });
    return projections[projections.length - 1]?.median || 0;
  }, [
    birthDate,
    initialDeposit,
    monthlyPayment,
    retirementAge,
    riskProfile,
    delay,
  ]);

  return (
    <div className="space-y-2">
      <div className="w-full space-y-6">
        <div className="space-y-2">
          <div className="mb-6 flex items-center gap-8">
            <DynamicFontTypography className="text-title2 lg:text-title2desktop">
              {t('retraite.investmentDelayImpact.title')}
            </DynamicFontTypography>
            <RippleIndicator
              content={t('retraite.investmentDelayImpact.rippleIndicator')}
            />
          </div>

          <Typography className="text-muted-foreground">
            {t('retraite.investmentDelayImpact.description')}
          </Typography>
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <SimpleCard
            className="flex flex-col"
            heading={t('retraite.investmentDelayImpact.impact.description')}
            icon="calendar-days"
            title={t('retraite.investmentDelayImpact.impact.title')}
          >
            <div className="flex h-full items-center">
              <Card className="flex h-fit w-full flex-col gap-4 border-gray-200 bg-gray-50 p-6">
                <div className="mb-4 flex justify-between">
                  <label htmlFor={id}>
                    {t('retraite.investmentDelayImpact.impact.label')}
                  </label>
                  <Badge className="self-start shadow" variant="secondary">
                    {t('common.year', {
                      value: delay[0] ?? investmentDelay.min,
                    })}
                  </Badge>
                </div>
                <Slider
                  id={id}
                  max={investmentDelay.max}
                  min={investmentDelay.min}
                  step={investmentDelay.step}
                  value={delay}
                  onValueChange={(value) => setDelay(value)}
                />
                <div className="flex w-full justify-between">
                  <span>
                    {t('common.year', {
                      value: investmentDelay.min,
                    })}
                  </span>
                  <span>
                    {t('common.year', {
                      value: investmentDelay.max,
                    })}
                  </span>
                </div>
              </Card>
            </div>
          </SimpleCard>

          <SimpleCard
            icon="trending-up"
            title={t('retraite.investmentDelayImpact.projection.title')}
          >
            <div className="grid gap-6 sm:grid-cols-2">
              <Card className="flex flex-col gap-6 p-6">
                <Typography className="text-balance" variant="title-7">
                  {t(
                    'retraite.investmentDelayImpact.projection.subtitle.withDelay',
                    {
                      value: t('common.year', {
                        value: delay[0] ?? investmentDelay.min,
                      }),
                    }
                  )}
                </Typography>
                <div className="mt-auto text-center">
                  <Typography
                    className="mb-0.5 text-destructive"
                    variant="title-4"
                  >
                    <AnimatedNumber
                      format={formatCurrency}
                      value={amountAtRetirementWithDelay - amountAtRetirement}
                    />
                  </Typography>
                  <Typography variant="helper">
                    {t('retraite.investmentDelayImpact.projection.loss')}
                  </Typography>
                  <Separator className="my-4" />
                  <Typography className="mb-0.5" variant="title-4">
                    <AnimatedNumber
                      format={formatCurrency}
                      value={amountAtRetirementWithDelay}
                    />
                  </Typography>
                  <Typography variant="helper">
                    {t('retraite.investmentDelayImpact.projection.expected')}
                  </Typography>
                </div>
              </Card>
              <Card
                className={cn(
                  'flex flex-col gap-6 border-primary/40 bg-accent p-6'
                )}
              >
                <Typography className="text-balance" variant="title-7">
                  {t(
                    'retraite.investmentDelayImpact.projection.subtitle.withoutDelay'
                  )}
                </Typography>
                <div className="mt-auto text-center">
                  <div className="flex h-max min-h-[3.25rem] items-center justify-center">
                    <Typography
                      className="whitespace-pre-wrap"
                      variant="title-6"
                    >
                      {t('retraite.investmentDelayImpact.projection.noLoss')}
                    </Typography>
                  </div>
                  <Separator className="my-4 bg-primary/40" />
                  <Typography className="mb-0.5" variant="title-4">
                    {formatCurrency(amountAtRetirement)}
                  </Typography>
                  <Typography variant="helper">
                    {t('retraite.investmentDelayImpact.projection.expected')}
                  </Typography>
                </div>
              </Card>
            </div>
          </SimpleCard>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Icon className="text-muted-foreground" name="info" size="xs" />
        <Typography className="text-muted-foreground" variant="caption">
          {t('germen.futureSavingsPlanner.disclaimer')}
        </Typography>
      </div>
    </div>
  );
};
